import { z } from 'zod';
import type { IntegrationEventMap } from './events.js';

const id = z.string().min(1).max(64);
const ref = z.string().min(1).max(500);
const capability = z.literal('economy.product-research.v1');
const note = z.string().max(280).optional();

/**
 * What corroborates one demand entry — AXIS 2 of ADR 0128.
 *
 * `.min(1)` on the array is the rule "every demand entry carries at least one signal" made
 * unrepresentable-if-broken rather than asserted in a comment. It is safe to require because the
 * producer attaches one in code for both derivations, never from the model's answer.
 *
 * There is deliberately NO strength/confidence/score field here. Strength is a fixed property of
 * `kind` and lives in `SIGNAL_STRENGTH`, in code. Putting it on the wire would let a producer send
 * a grade nobody computed, which is the entire failure mode ADR 0128 exists to close.
 */
const DemandSignalSchema = z
  .array(
    z.object({
      kind: z.enum(['supply-exists', 'vendor-claim', 'user-voice']),
      sourceUrl: z.string().min(1).max(2000).url(),
      sourceName: z.string().min(1).max(200),
      quote: z.string().min(1).max(1000),
      // `user-voice` only. Absent on anything the vendor said about its own market, because there
      // is no third party to name — and an empty author string would read as an anonymous person
      // rather than as no person.
      author: z.string().max(200).optional(),
      permalink: z.string().max(2000).url().optional(),
    }),
  )
  .min(1)
  .max(10);

/** Why a demand entry was refused (ADR 0128). Closed, because each member is a different fix. */
const DemandRefusalSchema = z.enum([
  'pages-never-said',
  'no-readable-blocks',
  'model-did-not-answer',
  'reply-did-not-parse',
  'block-we-never-sent',
  'quote-not-in-block',
  'block-belongs-to-no-page',
  'feature-we-did-not-keep',
  'need-not-stated',
]);

export interface EventDef {
  readonly payload: z.ZodTypeAny;
  readonly ordered: boolean;
  readonly title: string;
  readonly topic: string;
  readonly description: string;
  readonly fieldNotes?: Readonly<Record<string, string>>;
}

/**
 * A URL a consumer could actually go and fetch.
 *
 * `.url()` plus an explicit http(s) refinement, because Zod's `.url()` accepts `mailto:`,
 * `javascript:` and `ftp:` — and handing one of those to something whose job is "go and look at
 * this" is at best a failed fetch and at worst an instruction. The device already filters on the
 * same rule (`services/multi-agent/src/core/links.ts`); this is the half that cannot be bypassed by
 * a client that skips it.
 */
const fetchableUrl = z
  .string()
  .min(1)
  .max(2000)
  .url()
  // A regex on the string rather than `new URL(…).protocol`: this package targets no DOM and no
  // Node lib, so `URL` is not in scope here — and after `.url()` has already accepted it, the
  // scheme prefix is the only part left to check.
  .regex(/^https?:\/\//i, 'only http(s) URLs may be handed over to be looked into');

/** The single source of truth for every event that may cross the durable Event Hub. */
export const EVENT_REGISTRY = {
  /**
   * THE LINKS ARE THE SUBJECT. The operator pasted URLs and asked for them to be looked into.
   *
   * Deliberately separate from `multi.idea.captured.v1` rather than one shape with a `kind` flag:
   * the same URL means different things in the two, and a discriminator would move the "does this
   * concern me?" decision out of the hub's fan-out and into every consumer's handler, where it is
   * invisible, duplicated and untested.
   *
   * It names no destination and no action — "intake", "research", "crawl" are the CONSUMER's words
   * for what it does with a link it has been handed. Multi states what its operator meant and stops.
   *
   * UNORDERED: two captures are independent facts about different things. Ordering them would make
   * one slow consumer's queue hold up an unrelated capture for no correctness gain.
   */
  'multi.links.captured.v1': {
    payload: z.object({
      captureId: id,
      // `.min(1)` is the contract, not a nicety: "look into these" with nothing to look into is not
      // a fact, and a consumer handed it would write zero rows and report success.
      links: z.array(z.object({ url: fetchableUrl, note: z.string().max(500).optional() })).min(1).max(50),
      // `null`, not optional — "there was no prose" is a fact the operator's capture states, and an
      // absent field would let a producer that simply forgot look identical to one that reported it.
      context: z.string().max(20_000).nullable(),
      capturedAt: z.number().int().nonnegative(),
    }),
    ordered: false,
    title: 'Links Captured',
    topic: 'multi.capture',
    description:
      'The operator captured one or more links in Multi and asked for them to be looked into. The links are the subject; any prose around them is context. Names no destination — who acts on this is declared by consumers in SERVICE_HUB_MANIFEST, never by Multi.',
    fieldNotes: {
      captureId: "Multi's device-minted ULID for the capture — the business key, stable across re-syncs",
      links: 'THE SUBJECT: the URLs handed over to be looked into, in the order they were pasted',
      context: 'whatever prose surrounded them; null when the capture was links and nothing else',
      capturedAt: "the DEVICE's clock in epoch-ms — when it was written, not when it arrived",
    },
  },
  /**
   * THE PROPOSITION IS THE SUBJECT. `references` are evidence ABOUT the idea — prior art, an
   * article, a competitor — not things to go and look at in their own right. That difference is
   * the whole reason this is a second event and not a flag on the first.
   */
  'multi.idea.captured.v1': {
    payload: z.object({
      captureId: id,
      title: z.string().min(1).max(512),
      // The thing to be tested. `.min(1)` because an idea with no body is not an idea, and the one
      // column it lands in downstream (`ideas.brief`) is NOT NULL.
      body: z.string().min(1).max(20_000),
      // EVIDENCE, not subjects. Never route these anywhere as things to fetch on their own.
      references: z.array(z.object({ url: fetchableUrl })).max(50),
      comparables: z.array(z.string().min(1).max(200)).max(50),
      capturedAt: z.number().int().nonnegative(),
    }),
    ordered: false,
    title: 'Idea Captured',
    topic: 'multi.capture',
    description:
      'The operator captured an idea in Multi and asked for it to be validated. The body is the proposition under test; references and comparables are supporting material ABOUT it, not subjects in their own right.',
    fieldNotes: {
      captureId: "Multi's device-minted ULID for the capture",
      title: 'the idea in one line',
      body: 'THE SUBJECT: the idea in full, the thing to be tested',
      references: 'supporting links — evidence about the body, NOT things to go and look at',
      comparables: '"similar to X" the operator named',
      capturedAt: "the DEVICE's clock in epoch-ms",
    },
  },
  /**
   * THE THING TO BUILD IS THE SUBJECT — "make this exist", not "is this worth existing".
   *
   * A third event rather than a flag on the idea, for the same reason `links` and `idea` are two:
   * they differ by what the SUBJECT is, and that changes what a consumer does with it. An idea is
   * a proposition under test and the answer is a verdict. A prototype request is a construction
   * order and the answer is a thing that runs. Collapsing them would hand one consumer a payload
   * whose meaning depends on a discriminator, which is the shape this codebase already rejected.
   *
   * PUBLISHED WITH NO CONSUMER, deliberately (ADR 0125). The App Factory that would act on this
   * does not exist yet, so `consumersOf` returns nothing, the send sheet says "Nothing is set up
   * to receive this yet", and `hub_publish` writes the ledger row and fans out to nobody. That is
   * the expected steady state — but it is only safe because the ADR also binds the future
   * consumer to BACKFILL from `hub.events` on its first run rather than merely subscribing:
   * pgmq only receives sends made while a group was already subscribed, so subscribing alone
   * would silently abandon every request made before it shipped.
   *
   * `comparables` is deliberately ABSENT, unlike the idea event. Nothing consumes this yet, so
   * every field is a guess, and "like Linear's command palette" is design prose that belongs in
   * the brief. A field is easier to add later than to retract from a published contract.
   */
  'multi.prototype.captured.v1': {
    payload: z.object({
      captureId: id,
      title: z.string().min(1).max(512),
      // THE SUBJECT: what the thing should do. `.min(1)` because a build order with no brief is
      // not a build order — a consumer handed it could only guess, and guessing is how a
      // prototype gets built for a request nobody made.
      brief: z.string().min(1).max(20_000),
      // EVIDENCE about what to build — "like this page" — never subjects to fetch in their own
      // right. Same rule as the idea event's `references`, and the same reason.
      references: z.array(z.object({ url: fetchableUrl })).max(50),
      capturedAt: z.number().int().nonnegative(),
    }),
    ordered: false,
    title: 'Prototype Captured',
    topic: 'multi.capture',
    description:
      'The operator captured something in Multi and asked for it to be BUILT as a prototype. The brief is the subject — what the thing should do; references are inspiration about it, not subjects in their own right. Names no destination. Nothing consumes this yet (ADR 0125): it is recorded in the Hub ledger and delivered to no one until an App Factory subscribes AND backfills.',
    fieldNotes: {
      captureId: "Multi's device-minted ULID for the capture",
      title: 'what to build, in one line',
      brief: 'THE SUBJECT: what it should do, in the operator’s own words',
      references: 'links as inspiration ABOUT the thing to build, NOT things to go and fetch',
      capturedAt: "the DEVICE's clock in epoch-ms",
    },
  },
  'economy.research.requested.v1': {
    payload: z.object({
      researchRunId: id,
      // Every sweep kind is a research subject (ADR 0104 §3): one capability, a typed scope.
      // `params` carries the rest of the board's scope map (family, query, signalId, …).
      subject: z.object({
        type: z.enum(['product', 'objective', 'category', 'alternatives', 'demand', 'keyword', 'prompt']),
        id,
        label: z.string().max(200).optional(),
        params: z.record(z.string().max(200)).optional(),
      }),
      capability,
      contextManifestRef: ref,
    }),
    ordered: true,
    title: 'Economy Research Requested',
    topic: 'economy.research',
    description:
      'Economy Radar durably created a research run and made it available for fulfilment. It does not mean Agent Runner has queued or started execution.',
    fieldNotes: {
      researchRunId: 'Economy Radar research-run aggregate',
      subject: 'what to research: a product, an objective (one of the research markets), a category, alternatives, demand, a keyword or a free prompt — the sweep kinds, unified (ADR 0104)',
      capability: 'versioned domain capability Agent Runner must resolve',
      contextManifestRef: 'immutable run-context manifest reference',
    },
  },
  'economy.product.researched.v1': {
    payload: z.object({
      // The id Economy Radar minted and sent out on the request. It travels back unchanged, so no
      // stage derives a key — the join that P88/P89 died on cannot exist here.
      productId: id,
      researchRunId: id,
      outcome: z.enum(['evidenced', 'unreadable', 'nothing-found']),
      note,
      // The market the product SERVES, cited (ADR 0120). Replaces `industry`/`category`, which
      // asked what the product WAS — an axis on which every piece of AI SaaS answers
      // "Software & Services", so all 38 products on the board shared one category.
      //
      // Optional as a whole, and that is the honest shape: a page that never says who the product
      // is for yields no classification rather than a guess. `.min(1)` on `sourceUrl` and `quote`
      // for the same reason a feature's are (ADR 0115) — an unsourced category renders exactly like
      // a fabricated one.
      market: z
        .object({
          subcategory: z.string().min(1).max(120),
          customer: z.string().min(1).max(200),
          sourceUrl: z.string().min(1).max(2000).url(),
          sourceName: z.string().min(1).max(200),
          quote: z.string().min(1).max(1000),
          blockId: z.string().max(120).optional(),
        })
        .optional(),
      // Why there is NO market, present exactly when `market` is absent (ADR 0120 §2).
      //
      // A closed enum rather than prose, because each member is a different thing to go and fix and
      // the whole point is being able to count them. `pages-never-said` is the pipeline working;
      // every other member is a defect with a specific cause. Without this an unfiled product is
      // one undifferentiated blank — the same "not yet vs never" collapse ADR 0118 removed from the
      // delivery path, reintroduced one layer down and found on the first production sweep.
      marketRefused: z
        .enum([
          'pages-never-said',
          'no-readable-blocks',
          'model-did-not-answer',
          'reply-did-not-parse',
          'not-a-gics-group',
          'no-customer-named',
          'block-we-never-sent',
          'quote-not-in-block',
          'block-belongs-to-no-page',
          'quote-does-not-establish-the-market',
        ])
        .optional(),
      // Bounded on purpose. A finding on the wire is bounded where a pointer was not, and a
      // finding too large for the hub means the extraction is wrong — not that pointers were right.
      features: z
        .array(
          z.object({
            feature: z.string().min(1).max(200),
            need: z.string().max(300),
            // `.min(1)` on both, and it is the load-bearing part: ADR 0115 — nothing on screen
            // without a source. An empty string here renders as a sourced claim and is not one.
            sourceUrl: z.string().min(1).max(2000).url(),
            sourceName: z.string().min(1).max(200),
            quote: z.string().min(1).max(1000),
            blockId: z.string().max(120).optional(),
          }),
        )
        .max(50),
      featuresDropped: z.number().int().nonnegative(),
      sourcesRead: z.number().int().nonnegative(),
      // The demand half (ADR 0128), read off the same blocks in the same run.
      //
      // A DISCRIMINATED UNION, and that is load-bearing rather than tidy. `vendor-stated` requires
      // the quote it claims to be quoting; `inferred-from-feature` has no `quote` key at all, so a
      // model that invents one has it stripped here rather than one layer from the screen. The
      // failure this prevents is not a crash — it is an inference rendered with quotation marks
      // around it, which is indistinguishable from a finding.
      demand: z
        .array(
          z.discriminatedUnion('derivation', [
            z.object({
              derivation: z.literal('vendor-stated'),
              need: z.string().min(1).max(300),
              kind: z.enum(['pain', 'need', 'want']),
              sourceUrl: z.string().min(1).max(2000).url(),
              sourceName: z.string().min(1).max(200),
              quote: z.string().min(1).max(1000),
              blockId: z.string().max(120).optional(),
              signals: DemandSignalSchema,
            }),
            z.object({
              derivation: z.literal('inferred-from-feature'),
              need: z.string().min(1).max(300),
              kind: z.enum(['pain', 'need', 'want']),
              // The page the FEATURE came from. The need is not written on it — that is the point
              // of the derivation being `inferred`, and why there is no quote to give.
              sourceUrl: z.string().min(1).max(2000).url(),
              sourceName: z.string().min(1).max(200),
              fromFeature: z.string().min(1).max(200),
              signals: DemandSignalSchema,
            }),
          ]),
        )
        .max(30)
        .default([]),
      // Reasons, one per refusal — the count is `.length`. Two fields that must agree eventually
      // will not, and `featuresDropped` already shows what a bare count cannot tell anybody.
      demandRefusals: z.array(DemandRefusalSchema).max(60).default([]),
    }),
    ordered: true,
    title: 'Economy Product Researched',
    topic: 'economy.research',
    description:
      'Agent Runner established what it could about ONE product and carries the finding inline — the market it sells into and each feature, every one with its source and verbatim quote. Replaces the warehouse read-back as the delivery path (ADR 0118); the classification names the CUSTOMER\'s industry, not the vendor\'s (ADR 0120).',
    fieldNotes: {
      productId: 'the Economy Radar product id, minted by Economy Radar and returned unchanged',
      researchRunId: 'the research run that established this',
      outcome: 'evidenced = features were established; unreadable = the sources could not be read; nothing-found = they were read and supported nothing',
      note: 'human-readable detail for a non-evidenced outcome',
      market: 'the market this product SELLS INTO — the customer\'s GICS industry group, who they are in the page\'s own words, and the sentence that says so. Absent when the pages never say, which beats a guess (ADR 0120)',
      marketRefused: 'why there is no market — "pages-never-said" is the pipeline working; anything else names what we refused and why, so an unfiled product is never an undifferentiated blank',
      features: 'each with sourceUrl + verbatim quote inline, so a citation never depends on the warehouse being reachable',
      featuresDropped: 'how many the fact-check refused — "found 12, kept 9"',
      sourcesRead: 'pages actually read; zero alongside unreadable is the whole story in one number',
      demand: 'the needs read off the same pages, each saying HOW it was derived — "vendor-stated" with the sentence, or "inferred-from-feature" with the feature it was read off — and what corroborates it. Both derivations are vendor-derived; neither is proof the demand exists (ADR 0128)',
      demandRefusals: 'why entries were refused, one per refusal, so a dropped demand entry is countable AND actionable. The count is the length',
    },
  },
  'economy.research.cancelled.v1': {
    payload: z.object({
      researchRunId: id,
      reason: z.enum(['operator', 'superseded']),
      note: note,
    }),
    ordered: true,
    title: 'Economy Research Cancelled',
    topic: 'economy.research',
    description:
      'Economy Radar durably cancelled a research run. Agent Runner stops before dispatch, or asks a running agent to stop; a run that already completed is unaffected.',
    fieldNotes: {
      researchRunId: 'the Economy Radar research run being withdrawn',
      reason: 'operator = a person pressed cancel; superseded = a newer run replaced this one',
      note: 'optional human-readable context',
    },
  },
  'agent.run.queued.v1': {
    payload: z.object({ runId: id, researchRunId: id, capability, sourceEventId: id }),
    ordered: true,
    title: 'Agent Run Queued',
    topic: 'agent.run',
    description:
      'Agent Runner idempotently created the run for a source request. Redelivery returns the same logical run.',
    fieldNotes: {
      runId: 'Agent Runner run',
      researchRunId: 'owning Economy Radar research run',
      capability: 'resolved capability',
      sourceEventId: 'effect-idempotency key from the request event',
    },
  },
  'agent.run.started.v1': {
    payload: z.object({ runId: id, researchRunId: id, capability }),
    ordered: true,
    title: 'Agent Run Started',
    topic: 'agent.run',
    description: 'Execution of the resolved Economy research capability began.',
  },
  'agent.run.plan.created.v1': {
    payload: z.object({
      runId: id,
      researchRunId: id,
      planRef: ref,
      /** The step labels, in order. What lets the board say "step 3 of 7" honestly. */
      steps: z.array(z.string().min(1).max(120)).max(50).optional(),
    }),
    ordered: true,
    title: 'Agent Run Plan Created',
    topic: 'agent.run',
    description: 'The agent produced or revised a structured, retrievable work plan.',
  },
  'agent.run.milestone.completed.v1': {
    payload: z.object({
      runId: id,
      researchRunId: id,
      milestone: z.string().min(1).max(160),
      sequence: z.number().int().positive(),
    }),
    ordered: true,
    title: 'Agent Run Milestone Completed',
    topic: 'agent.run',
    description: 'A domain-meaningful research milestone completed.',
  },
  'agent.run.completed.v1': {
    payload: z.object({
      runId: id,
      researchRunId: id.optional(),
      capability: z.string().min(1).max(160).optional(),
      resultRef: ref.optional(),
    }),
    ordered: true,
    title: 'Agent Run Completed',
    topic: 'agent.run',
    description:
      'An Agent Runner run finished successfully. Economy research completions carry a typed result reference; legacy browser-agent completions remain valid with runId only.',
    fieldNotes: { runId: 'the completed run', resultRef: 'typed result artifact reference' },
  },
  'agent.run.failed.v1': {
    payload: z.object({
      runId: id,
      researchRunId: id,
      capability,
      error: z.object({ kind: z.string().min(1).max(64), message: z.string().max(2000) }),
    }),
    ordered: true,
    title: 'Agent Run Failed',
    topic: 'agent.run',
    description: 'An Economy research run reached a visible terminal failure.',
  },
} as const satisfies Record<keyof IntegrationEventMap, EventDef>;

/**
 * Published events that deliberately have NO consumer yet (ADR 0125).
 *
 * The fleet's standing invariant is that every published event has at least one producer AND at
 * least one consumer, because an event nobody receives is almost always a wiring mistake — a
 * consumer that was renamed, or one that was never subscribed. That check has caught real faults
 * and it stays.
 *
 * This is the narrow, named exception: an event whose producer ships BEFORE its consumer exists,
 * on purpose. Listing it here is a claim with a reason attached, and the tests hold it to both —
 * an entry whose event has since gained a consumer FAILS, so the exemption cannot quietly outlive
 * the situation that justified it.
 *
 * The obligation that comes with being on this list: whatever eventually subscribes must ALSO
 * backfill from `hub.events`, because `hub_publish` fans out only to groups already subscribed and
 * pgmq has no record of sends made before that. Subscribing alone starts at "now" and abandons
 * everything published in the meantime.
 */
export const EVENTS_AWAITING_A_CONSUMER = {
  'multi.prototype.captured.v1':
    'The App Factory that would build these does not exist yet — it is not in this manifest, the deploy catalogue, or services/. Multi can already state the operator\'s intent, so the event is emitted and kept in the Hub ledger (ADR 0125, AF-MULTI67 tracks the backfill obligation).',
} as const satisfies Partial<Record<keyof IntegrationEventMap, string>>;

/** Is this event knowingly without a consumer, rather than accidentally? */
export function awaitsAConsumer(name: string): boolean {
  return Object.prototype.hasOwnProperty.call(EVENTS_AWAITING_A_CONSUMER, name);
}

/** Events referenced by a planned flow but not yet publishable. */
export const PLANNED_EVENTS = {
  'economy.research.fulfilled.v1': {
    title: 'Research Fulfilled',
    description: 'Legacy sweep fulfilment fact; strategic research uses agent.run.completed.v1.',
  },
  'idea.research.requested.v1': {
    title: 'Idea Research Requested',
    description:
      'The Idea Engine asks the shared Agent Runner to research an idea on its behalf — the idea-initiated counterpart to economy.research.requested.v1. Not yet wired: it becomes a published event, with a real Idea Engine producer + an idempotent Agent Runner consumer, under DEVOP-38, at which point the idea→agent-research saga returns to the flow catalogue (it was removed in the Event Hub de-simulation because no service produced it — a saga must depict a real, wired flow).',
  },
} as const satisfies Record<string, { title: string; description: string }>;

/**
 * What the operator asked for in Multi → the event that states it.
 *
 * The ONE place the two are tied together, so the device, the gateway and the UI cannot disagree
 * about which intent becomes which event. It maps intent → EVENT, never intent → SERVICE: who acts
 * on an event is declared by consumers in `SERVICE_HUB_MANIFEST`, and Multi finds out by asking
 * `consumersOf`, exactly like anything else would.
 *
 * That indirection is the point. A third service subscribing is a manifest row; Multi's dispatch
 * path and its UI are unchanged, because neither has ever named a destination.
 */
export const MULTI_DISPATCH_EVENTS = {
  /** The links ARE the subject: "look into these". */
  links: 'multi.links.captured.v1',
  /** The proposition is the subject: "is this worth building?". */
  idea: 'multi.idea.captured.v1',
  /**
   * The thing to build is the subject: "make this exist".
   *
   * Nothing consumes it yet, and that is a recorded decision rather than an oversight (ADR 0125).
   * Because `consumersOf` is the only source of destinations, the UI tells the truth about this
   * with no special case anywhere.
   */
  prototype: 'multi.prototype.captured.v1',
} as const satisfies Record<string, string>;

export type MultiDispatchIntent = keyof typeof MULTI_DISPATCH_EVENTS;

export function isPublished(name: string): name is keyof IntegrationEventMap {
  return Object.prototype.hasOwnProperty.call(EVENT_REGISTRY, name);
}

export function isKnownEvent(name: string): boolean {
  return isPublished(name) || Object.prototype.hasOwnProperty.call(PLANNED_EVENTS, name);
}

export interface ServiceHubDef {
  readonly name: string;
  readonly produces: readonly string[];
  readonly consumes: readonly string[];
  readonly reacts?: boolean;
  readonly ordered?: boolean;
}

const Q = {
  economyResearchRequested: 'economy.research.requested.v1',
  economyProductResearched: 'economy.product.researched.v1',
  economyResearchCancelled: 'economy.research.cancelled.v1',
  agentRunQueued: 'agent.run.queued.v1',
  agentRunStarted: 'agent.run.started.v1',
  agentRunPlanCreated: 'agent.run.plan.created.v1',
  agentRunMilestoneCompleted: 'agent.run.milestone.completed.v1',
  agentRunCompleted: 'agent.run.completed.v1',
  agentRunFailed: 'agent.run.failed.v1',
  multiLinksCaptured: 'multi.links.captured.v1',
  multiIdeaCaptured: 'multi.idea.captured.v1',
  multiPrototypeCaptured: 'multi.prototype.captured.v1',
} as const;

// Only the REALLY-wired hub participants (ADR 0067 / 0099 / 0102): each entry below has a live
// producer (via the transactional outbox + relay) or a live consumer drain loop in its service —
// Agent Runner ⇄ Economy Radar is the fulfilment round-trip, Idea Engine observes completions. The
// harness `hub.test.*` events and the services that only ever participated through them (Talent Hub,
// People Engine, Finance, Mission Tracker's hub read, Observability, Command Center) were removed in
// the Event Hub de-simulation — a service re-enters this manifest the moment it ships a real
// producer/consumer, not before.
export const SERVICE_HUB_MANIFEST = {
  /**
   * Multi is a PRODUCER ONLY, and deliberately consumes nothing.
   *
   * It states what its operator meant and stops. `produces` is the whole of its relationship
   * with the hub — it does not name Economy Radar or the Idea Engine anywhere, and it finds out
   * who acts on a capture by asking `consumersOf` against the rows BELOW, exactly as anything
   * else would. A third subscriber is a line in another service's `consumes`; Multi's dispatch
   * path and its send sheet are unchanged (APP-8 §2).
   */
  'multi-agent': {
    name: 'Multi',
    // The third is produced with NOTHING consuming it, deliberately (ADR 0125). Declaring it here
    // is what makes that visible — the system graph gains a produced event with no consumer edge,
    // which is the honest picture rather than an event nobody can see is being emitted.
    produces: [Q.multiLinksCaptured, Q.multiIdeaCaptured, Q.multiPrototypeCaptured],
    consumes: [],
  },
  'agent-runner': {
    name: 'Agent Runner',
    produces: [
      Q.agentRunQueued,
      Q.agentRunStarted,
      Q.agentRunPlanCreated,
      Q.agentRunMilestoneCompleted,
      Q.agentRunCompleted,
      Q.agentRunFailed,
      // The finding itself (ADR 0118). `agent.run.completed.v1` still means "this execution
      // finished" and still carries its `resultRef`; it is no longer how a RESULT is delivered.
      Q.economyProductResearched,
    ],
    consumes: [Q.economyResearchRequested, Q.economyResearchCancelled],
    reacts: true,
  },
  'economy-radar': {
    name: 'Economy Radar',
    produces: [Q.economyResearchRequested, Q.economyResearchCancelled],
    consumes: [
      Q.agentRunQueued,
      Q.agentRunStarted,
      Q.agentRunPlanCreated,
      Q.agentRunMilestoneCompleted,
      Q.agentRunCompleted,
      Q.agentRunFailed,
      Q.economyProductResearched,
      // "Look into these" — the links ARE the subject, so each one becomes a product on the
      // board at `discovered`, which is what puts it in the research pump's queue (APP-8 §8.3).
      Q.multiLinksCaptured,
    ],
    reacts: true,
  },
  'idea-engine': {
    name: 'Idea Engine',
    produces: [],
    // The idea captured in Multi lands on the Venture Fleet as PENDING — the same state a
    // locally-created idea sits in until someone evaluates it. Intake is not evaluation, and
    // nothing here claims it is (APP-8 §8.3).
    consumes: [Q.agentRunCompleted, Q.multiIdeaCaptured],
    reacts: true,
  },
} as const satisfies Record<string, ServiceHubDef>;

export function producersOf(name: string): string[] {
  return Object.entries(SERVICE_HUB_MANIFEST)
    .filter(([, service]) => (service.produces as readonly string[]).includes(name))
    .map(([serviceId]) => serviceId);
}

export function consumersOf(name: string): string[] {
  return Object.entries(SERVICE_HUB_MANIFEST)
    .filter(([, service]) => (service.consumes as readonly string[]).includes(name))
    .map(([serviceId]) => serviceId);
}

export function serviceName(id: string): string {
  return (SERVICE_HUB_MANIFEST as Record<string, ServiceHubDef>)[id]?.name ?? id;
}

export interface MonitorService {
  readonly id: string;
  readonly name: string;
  readonly produces: readonly string[];
  readonly consumes: readonly string[];
  readonly reacts: boolean;
  readonly ordered: boolean;
}

/**
 * One node of the Monitor's inner ring.
 *
 * The node is an EVENT, not a channel — the hub routes by event NAME (subscriptions carry names;
 * `topic` is a documentation-level grouping several events share). Labelling each node with its
 * `topic` therefore drew nine nodes captioned "agent.run" ×6 and "economy.research" ×3, which reads
 * as duplicates of one thing rather than as the distinct facts they are. `label` is now the part of
 * the name that DISTINGUISHES it, and `topic` rides along so the map can still group by channel.
 */
export interface MonitorTopic {
  /** The event name — the hub's real routing unit. */
  readonly key: string;
  /** Short and distinct: the name minus the channel it shares and the version suffix. */
  readonly label: string;
  /** The channel this event belongs to, shared with its siblings. */
  readonly topic: string;
  /** Human title, for the node's tooltip. */
  readonly title: string;
}

/**
 * The distinguishing tail of an event name: drop the version, then the leading segments it shares
 * with its topic. `agent.run.queued.v1` under `agent.run` → `queued`; `economy.product.researched.v1`
 * under `economy.research` → `product.researched` (only `economy` is shared).
 */
export function eventShortLabel(name: string, topic: string): string {
  const bare = name.replace(/\.v1$/, '');
  const parts = bare.split('.');
  const topicParts = topic.split('.');
  let shared = 0;
  while (shared < topicParts.length && shared < parts.length - 1 && parts[shared] === topicParts[shared]) shared += 1;
  return parts.slice(shared).join('.');
}

/**
 * The radial map the Hub Monitor draws — the REAL fleet + the REAL topics, straight from the
 * manifest and the registry (the Monitor is a read-only viewer of live traffic; ADR 0067). Topics
 * are keyed by event name and labelled by the registry `topic`.
 */
export function monitorTopology(): {
  services: readonly MonitorService[];
  topics: readonly MonitorTopic[];
} {
  const entries = Object.entries(SERVICE_HUB_MANIFEST) as [string, ServiceHubDef][];
  const services = entries.map(([id, service]): MonitorService => ({
    id,
    name: service.name,
    produces: [...service.produces],
    consumes: [...service.consumes],
    reacts: service.reacts ?? false,
    ordered: service.ordered ?? false,
  }));
  // Sorted by channel so a topic's events sit together on the ring — the grouping the shared label
  // used to imply, now expressed by position instead of by nine nodes sharing three words.
  const topics = INTEGRATION_EVENT_NAMES.map((key): MonitorTopic => ({
    key,
    label: eventShortLabel(key, EVENT_REGISTRY[key].topic),
    topic: EVENT_REGISTRY[key].topic,
    title: EVENT_REGISTRY[key].title,
  })).sort((a, b) => a.topic.localeCompare(b.topic) || a.key.localeCompare(b.key));
  return { services, topics };
}

export interface LifecycleStateDef {
  readonly event: string;
  readonly state: string;
  readonly kind: 'initial' | 'transition' | 'success' | 'failure';
}

export interface LifecycleDef {
  readonly id: string;
  readonly title: string;
  readonly service: string;
  readonly aggregate: string;
  readonly description: string;
  readonly states: readonly LifecycleStateDef[];
  readonly transitions: readonly { readonly from: string; readonly to: string }[];
}

export const LIFECYCLES: readonly LifecycleDef[] = [
  {
    id: 'agent-run',
    title: 'Agent Run',
    service: 'agent-runner',
    aggregate: 'runId',
    description: 'Queued, started, then completed or failed; ordered per runId.',
    states: [
      { event: Q.agentRunQueued, state: 'queued', kind: 'initial' },
      { event: Q.agentRunStarted, state: 'started', kind: 'transition' },
      { event: Q.agentRunCompleted, state: 'completed', kind: 'success' },
      { event: Q.agentRunFailed, state: 'failed', kind: 'failure' },
    ],
    transitions: [
      { from: 'queued', to: 'started' },
      { from: 'started', to: 'completed' },
      { from: 'started', to: 'failed' },
    ],
  },
  {
    id: 'economy-research',
    title: 'Economy Research',
    service: 'economy-radar',
    aggregate: 'researchRunId',
    description: 'A strategic research request fulfilled by the shared Agent Runner.',
    states: [
      { event: Q.economyResearchRequested, state: 'requested', kind: 'initial' },
      { event: Q.agentRunQueued, state: 'queued', kind: 'transition' },
      { event: Q.agentRunStarted, state: 'running', kind: 'transition' },
      { event: Q.agentRunCompleted, state: 'ready', kind: 'success' },
      { event: Q.agentRunFailed, state: 'failed', kind: 'failure' },
      { event: Q.economyResearchCancelled, state: 'cancelled', kind: 'failure' },
    ],
    transitions: [
      { from: 'requested', to: 'queued' },
      { from: 'queued', to: 'running' },
      { from: 'running', to: 'ready' },
      { from: 'running', to: 'failed' },
      // Cancellable at every stage before a terminal one — the board's cancel button has
      // always worked mid-run, and a sweep is now an agent run (ADR 0104 §4).
      { from: 'requested', to: 'cancelled' },
      { from: 'queued', to: 'cancelled' },
      { from: 'running', to: 'cancelled' },
    ],
  },
];

export interface FlowStepDef {
  readonly event: string;
  readonly from: string;
  readonly to: string;
  readonly note?: string;
}

export interface FlowDef {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly steps: readonly FlowStepDef[];
}

export const FLOWS: readonly FlowDef[] = [
  {
    id: 'economy-product-research',
    name: 'Economy Product Research',
    description:
      'Economy Radar requests strategic research; the shared Agent Runner fulfils it and the result returns. The intermediate run lifecycle (queued → started → completed | failed) is the Agent Run / Economy Research lifecycle, not saga hops — this saga shows only the cross-service request and its fulfilment.',
    steps: [
      {
        event: Q.economyResearchRequested,
        from: 'economy-radar',
        to: 'agent-runner',
        note: 'Economy Radar asks the Agent Runner to research a product',
      },
      {
        event: Q.economyProductResearched,
        from: 'agent-runner',
        to: 'economy-radar',
        note: 'the finding itself returns — the market it sells into and each feature, every one with its source and quote, carried inline (ADR 0118/0120)',
      },
      {
        event: Q.agentRunCompleted,
        from: 'agent-runner',
        to: 'economy-radar',
        note: 'the execution finished; the Idea Engine also observes completions independently. A lifecycle fact, no longer the delivery of a result',
      },
    ],
  },
  {
    id: 'multi-capture-dispatch',
    name: 'Multi Capture Dispatch',
    description:
      "What the operator wrote on their phone reaches the service that can act on it. ONE HOP, TWICE — the two intents are separate events with separate consumers, and that separation is the design rather than an accident of who happens to subscribe: in `links.captured` a URL is the SUBJECT, in `idea.captured` it is EVIDENCE about the body. Multi names neither destination; the arrows below are derived from what each consumer declared, which is the same thing Multi's send sheet reads to tell the operator where a capture is going (APP-8).",
    steps: [
      {
        event: Q.multiLinksCaptured,
        from: 'multi-agent',
        to: 'economy-radar',
        note: '"look into these" — each URL becomes a product on the board at `discovered`, which is what puts it in the research pump\'s queue, so this flow feeds the one above',
      },
      {
        event: Q.multiIdeaCaptured,
        from: 'multi-agent',
        to: 'idea-engine',
        note: '"is this worth building" — the idea lands on the Venture Fleet as PENDING. Intake is not evaluation, and nothing downstream claims it is',
      },
    ],
  },
];

// ── titles + trace projection (Event Inspector, DEVOP-40) ─────────────────────────────
/** Human title for any known event name (published or planned); falls back to the raw name. */
export function titleOf(name: string): string {
  if (isPublished(name)) return EVENT_REGISTRY[name].title;
  const planned = (PLANNED_EVENTS as Record<string, { title: string }>)[name];
  return planned ? planned.title : name;
}

/** Short topic label for an event name; the registry topic when published, else a name-derived fallback. */
export function topicOf(name: string): string {
  return isPublished(name) ? EVENT_REGISTRY[name].topic : name.replace(/\.v1$/, '');
}

/**
 * ── A correlation trace is a FOLD through the state machine, not a queue of steps ──────
 *
 * (ADR 0067, Amendment 2026-08-09.) The first cut of this derivation returned every transition
 * target of every state each event in the trace enters, minus whatever was literally present. That
 * reads LIFECYCLES as a checklist. It is a state machine: `economy-research` forks three ways out of
 * `running`, and `cancelled` is reachable from every non-terminal state. So a run that completed
 * successfully kept advertising `Agent Run Failed` and `Economy Research Cancelled` as still-expected
 * — two endings the successful one had already foreclosed — and the viewer whose entire job is to
 * answer *done or stuck?* answered "stuck" for every finished run.
 *
 * What follows folds the trace through the machine instead, and stays pure + derived (nothing is
 * stored), so the API, both Hub pages and the generated catalogue doc cannot drift from the registry.
 */

/**
 * One lifecycle state resolved for display: the event that enters it, what kind of state it is, and
 * whether reaching it ENDS the lifecycle.
 *
 * `terminal` is DERIVED — a state is terminal iff no transition leaves it. Declaring it on the state
 * would be a second place to forget, and the thing forgotten would be the one deciding whether the
 * UI says "done" or "still waiting".
 */
export interface LifecycleStep {
  readonly event: string;
  readonly title: string;
  readonly state: string;
  readonly kind: LifecycleStateDef['kind'];
  readonly terminal: boolean;
}

/** Where one lifecycle stands in a given trace, and what may — or can no longer — follow. */
export interface LifecycleProjection {
  readonly id: string;
  readonly title: string;
  readonly service: string;
  readonly aggregate: string;
  /** The states this trace actually entered, oldest first. */
  readonly path: readonly LifecycleStep[];
  /** Where it stands now — the last state entered. */
  readonly current: LifecycleStep;
  /** `current` is terminal: this lifecycle is over and nothing further can follow. */
  readonly settled: boolean;
  /** The ending it settled on, when settled. */
  readonly outcome: 'success' | 'failure' | null;
  /** The mutually-exclusive branches still open from `current`. Empty once settled. */
  readonly next: readonly LifecycleStep[];
  /** States that were reachable when this trace began but can no longer happen. */
  readonly foreclosed: readonly LifecycleStep[];
  /**
   * States this trace's events named that the machine cannot reach from where it stood — an event
   * arriving after its lifecycle had moved past it (a cancel landing on an already-completed run) or
   * out of order. Surfaced rather than walked: silently taking an impossible edge would report that
   * completed run as cancelled, which is the same class of lie this projection exists to remove.
   */
  readonly offMachine: readonly LifecycleStep[];
}

/** One hop of a flow, with whether this trace has seen it. */
export interface FlowStepView {
  readonly event: string;
  readonly title: string;
  readonly from: string;
  readonly to: string;
  readonly note?: string;
  readonly seen: boolean;
}

/** How far one cross-service flow got in a given trace. */
export interface FlowProjection {
  readonly id: string;
  readonly name: string;
  readonly steps: readonly FlowStepView[];
  readonly complete: boolean;
  /** Index of the furthest hop seen — hops before it that are unseen were SKIPPED, not pending. */
  readonly furthest: number;
  /** The first unseen hop AHEAD of `furthest`; null when nothing further is owed. */
  readonly next: FlowStepView | null;
}

/** One event that may still legitimately follow, and where that claim comes from. */
export interface PossibleNext {
  readonly event: string;
  readonly title: string;
  /** Provenance, e.g. `flow:economy-product-research` or `lifecycle:agent-run`. */
  readonly via: string;
  /** `one-of` when it is one of several mutually-exclusive branches; `only` when it is the sole successor. */
  readonly branch: 'only' | 'one-of';
}

/**
 * Everything the registry can say about one correlation trace.
 *
 * Known limit, stated rather than hidden: one correlation projects ONE instance per lifecycle. A
 * correlation that ever carried two runs of the same aggregate would show the latest state; splitting
 * by aggregate id would need that id on the ledger row, which today carries only `orderingKey` — and
 * that key means `runId` to one producer and `researchRunId` to another, so keying on it would split
 * a single economy-research lifecycle in two.
 */
export interface TraceProjection {
  readonly lifecycles: readonly LifecycleProjection[];
  readonly flows: readonly FlowProjection[];
  /** Every lifecycle this trace touched has ended — nothing further will arrive on this thread. */
  readonly settled: boolean;
  /** Flattened + de-duplicated across lifecycles and flows. Empty once `settled`. */
  readonly possibleNext: readonly PossibleNext[];
}

function stepOf(lifecycle: LifecycleDef, state: LifecycleStateDef): LifecycleStep {
  return {
    event: state.event,
    title: titleOf(state.event),
    state: state.state,
    kind: state.kind,
    terminal: !lifecycle.transitions.some((t) => t.from === state.state),
  };
}

/** Every state reachable from `from` by any number of transitions. */
function reachableFrom(lifecycle: LifecycleDef, from: string): Set<string> {
  const out = new Set<string>();
  const queue = [from];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    for (const t of lifecycle.transitions) {
      if (t.from !== current || out.has(t.to)) continue;
      out.add(t.to);
      queue.push(t.to);
    }
  }
  return out;
}

/** Fold one trace through one lifecycle; null when the trace never touched it. */
function projectLifecycle(lifecycle: LifecycleDef, names: readonly string[]): LifecycleProjection | null {
  const byState = new Map(lifecycle.states.map((s) => [s.state, s]));
  const reach = new Map<string, Set<string>>();
  const ahead = (state: string): Set<string> => {
    const memo = reach.get(state);
    if (memo) return memo;
    const set = reachableFrom(lifecycle, state);
    reach.set(state, set);
    return set;
  };

  const path: LifecycleStep[] = [];
  const strayed = new Map<string, LifecycleStateDef>();
  for (const name of names) {
    const candidates = lifecycle.states.filter((s) => s.event === name);
    if (candidates.length === 0) continue;
    const last = path[path.length - 1];
    if (!last) {
      const first = candidates[0];
      if (first) path.push(stepOf(lifecycle, first));
      continue;
    }
    // The hub is at-least-once (ADR 0101): a redelivered event must not advance the path.
    if (candidates.some((s) => s.state === last.state)) continue;
    // Advance only along an edge the machine actually has. Reachability is TRANSITIVE on purpose —
    // a trace whose intermediate events have aged out still legitimately jumps ahead — but an event
    // naming a state that cannot be reached from here at all is off the machine, not a transition.
    const forward = ahead(last.state);
    const chosen = candidates.find((s) => forward.has(s.state));
    if (!chosen) {
      for (const c of candidates) strayed.set(c.state, c);
      continue;
    }
    path.push(stepOf(lifecycle, chosen));
  }
  const start = path[0];
  const current = path[path.length - 1];
  if (!start || !current) return null;

  const next = lifecycle.transitions
    .filter((t) => t.from === current.state)
    .map((t) => byState.get(t.to))
    .filter((s): s is LifecycleStateDef => s !== undefined)
    .map((s) => stepOf(lifecycle, s));

  // Foreclosed = what this trace could once have reached, minus what it can still reach, minus what
  // it already walked. This is where `failed` and `cancelled` belong on a completed run: they did
  // not fail to arrive, they can no longer happen — and that distinction is the whole fix.
  const wasReachable = ahead(start.state);
  const stillReachable = ahead(current.state);
  const walked = new Set(path.map((s) => s.state));
  const foreclosed = lifecycle.states
    .filter((s) => wasReachable.has(s.state) && !stillReachable.has(s.state) && !walked.has(s.state))
    .map((s) => stepOf(lifecycle, s));
  const offMachine = lifecycle.states.filter((s) => strayed.has(s.state)).map((s) => stepOf(lifecycle, s));

  const outcome = current.terminal && (current.kind === 'success' || current.kind === 'failure') ? current.kind : null;
  return {
    id: lifecycle.id,
    title: lifecycle.title,
    service: lifecycle.service,
    aggregate: lifecycle.aggregate,
    path,
    current,
    settled: current.terminal,
    outcome,
    next,
    foreclosed,
    offMachine,
  };
}

/** Mark a flow's hops against a trace; null when none of its events appeared. */
function projectFlow(flow: FlowDef, present: ReadonlySet<string>): FlowProjection | null {
  const steps = flow.steps.map((s): FlowStepView => ({
    event: s.event,
    title: titleOf(s.event),
    from: s.from,
    to: s.to,
    ...(s.note !== undefined ? { note: s.note } : {}),
    seen: present.has(s.event),
  }));
  if (!steps.some((s) => s.seen)) return null;
  // The first unseen hop AHEAD of the furthest one seen. Taking the first unseen hop outright meant a
  // trace holding only the FINDING advertised the REQUEST that caused it as still to come — the same
  // lie removed from lifecycles, reintroduced a level up. An earlier unseen hop was skipped (aged
  // out of the ledger, or never emitted); it is not owed.
  let furthest = -1;
  steps.forEach((s, i) => { if (s.seen) furthest = i; });
  const next = steps.slice(furthest + 1).find((s) => !s.seen) ?? null;
  return { id: flow.id, name: flow.name, steps, complete: steps.every((s) => s.seen), furthest, next };
}

/**
 * Project a correlation trace — given its event names in occurrence order — through every lifecycle
 * and flow it touched. Pure and total: unknown names simply match nothing.
 */
export function projectTrace(presentNames: readonly string[]): TraceProjection {
  const present = new Set(presentNames);
  const lifecycles = LIFECYCLES.map((lc) => projectLifecycle(lc, presentNames)).filter(
    (p): p is LifecycleProjection => p !== null,
  );
  const flows = FLOWS.map((f) => projectFlow(f, present)).filter((p): p is FlowProjection => p !== null);
  const settled = lifecycles.length > 0 && lifecycles.every((l) => l.settled);

  // De-duplicated on EVENT NAME, not on (event, via). The two machines overlap — `agent.run.started.v1`
  // is the sole successor of `queued` in agent-run and one of two in economy-research — so keying on
  // provenance listed the same event twice with contradictory branch labels. `one-of` wins when the
  // sources disagree: if any machine says the event is one of several alternatives, it is not the
  // guaranteed next thing, and claiming otherwise is the failure mode this whole change is about.
  const possibleNext: PossibleNext[] = [];
  const byEvent = new Map<string, number>();
  const push = (step: { event: string; title: string }, via: string, branch: PossibleNext['branch']): void => {
    const at = byEvent.get(step.event);
    if (at !== undefined) {
      const existing = possibleNext[at];
      if (existing && branch === 'one-of' && existing.branch === 'only') possibleNext[at] = { ...existing, branch };
      return;
    }
    byEvent.set(step.event, possibleNext.length);
    possibleNext.push({ event: step.event, title: step.title, via, branch });
  };
  for (const l of lifecycles) {
    for (const step of l.next) push(step, `lifecycle:${l.id}`, l.next.length > 1 ? 'one-of' : 'only');
  }
  // Only while something is still running. Once every lifecycle has ended, an unseen hop is one that
  // DID NOT ARRIVE — calling it "next" would repeat the same lie one level up from the lifecycle.
  if (!settled) {
    for (const f of flows) if (f.next) push(f.next, `flow:${f.id}`, 'only');
  }
  return { lifecycles, flows, settled, possibleNext };
}

// ── derived: names, constants, payload registry, name schema ───────────────────────────
export const INTEGRATION_EVENT_NAMES = Object.keys(
  EVENT_REGISTRY,
) as unknown as readonly (keyof IntegrationEventMap)[];

export const INTEGRATION_EVENTS = {
  multiLinksCaptured: Q.multiLinksCaptured,
  multiIdeaCaptured: Q.multiIdeaCaptured,
  multiPrototypeCaptured: Q.multiPrototypeCaptured,
  economyResearchRequested: Q.economyResearchRequested,
  economyProductResearched: Q.economyProductResearched,
  economyResearchCancelled: Q.economyResearchCancelled,
  agentRunQueued: Q.agentRunQueued,
  agentRunStarted: Q.agentRunStarted,
  agentRunPlanCreated: Q.agentRunPlanCreated,
  agentRunMilestoneCompleted: Q.agentRunMilestoneCompleted,
  agentRunCompleted: Q.agentRunCompleted,
  agentRunFailed: Q.agentRunFailed,
} as const satisfies Record<string, keyof IntegrationEventMap>;

export const INTEGRATION_EVENT_PAYLOAD_SCHEMAS = Object.fromEntries(
  (Object.entries(EVENT_REGISTRY) as [keyof IntegrationEventMap, EventDef][]).map(([key, value]) => [
    key,
    value.payload,
  ]),
) as Record<keyof IntegrationEventMap, z.ZodTypeAny>;

export const IntegrationEventNameSchema = z.enum(
  Object.keys(EVENT_REGISTRY) as [keyof IntegrationEventMap, ...(keyof IntegrationEventMap)[]],
);
export type IntegrationEventName = z.infer<typeof IntegrationEventNameSchema>;

interface ZodIntrospect {
  readonly _def?: { readonly typeName?: string; readonly innerType?: z.ZodTypeAny };
}

function baseType(schema: z.ZodTypeAny): string {
  let current: z.ZodTypeAny = schema;
  let definition = (current as unknown as ZodIntrospect)._def;
  while (
    definition &&
    (definition.typeName === 'ZodOptional' ||
      definition.typeName === 'ZodDefault' ||
      definition.typeName === 'ZodNullable') &&
    definition.innerType
  ) {
    current = definition.innerType;
    definition = (current as unknown as ZodIntrospect)._def;
  }
  const typeName = definition?.typeName;
  if (typeName === 'ZodString') return 'string';
  if (typeName === 'ZodNumber') return 'number';
  if (typeName === 'ZodBoolean') return 'boolean';
  if (typeName === 'ZodEnum') return 'enum';
  if (typeName === 'ZodArray') return 'array';
  if (typeName === 'ZodObject') return 'object';
  return 'unknown';
}

export interface PayloadFieldInfo {
  readonly field: string;
  readonly type: string;
  readonly note?: string;
}

export function payloadFields(name: keyof IntegrationEventMap): readonly PayloadFieldInfo[] {
  const definition: EventDef = EVENT_REGISTRY[name];
  const schema = definition.payload;
  if (!(schema instanceof z.ZodObject)) return [];
  const shape = schema.shape as Record<string, z.ZodTypeAny>;
  return Object.entries(shape).map(([field, fieldSchema]) => {
    const type = baseType(fieldSchema) + (fieldSchema.isOptional() ? '?' : '');
    const fieldNote = definition.fieldNotes?.[field];
    return fieldNote ? { field, type, note: fieldNote } : { field, type };
  });
}

/** Result of checking a stored payload against its registry schema (Event Inspector). */
export interface PayloadValidity {
  readonly valid: boolean;
  readonly issues: readonly string[];
}
/**
 * Parse a stored payload against the event's registry Zod schema — the Inspector shows both the shape
 * and whether the recorded payload is contract-valid. Unknown event names are reported as invalid
 * (there is no schema to hold them to) rather than throwing.
 */
export function validatePayload(name: string, payload: unknown): PayloadValidity {
  if (!isPublished(name)) return { valid: false, issues: [`unknown event "${name}" — no registry schema`] };
  const result = EVENT_REGISTRY[name].payload.safeParse(payload);
  if (result.success) return { valid: true, issues: [] };
  return { valid: false, issues: result.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`) };
}
